import * as React from "react";
import { useState } from "react";
import { useNavigate } from "react-router";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Field, FieldLabel, FieldError } from "@/components/ui/field";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

const loginSchema = z.object({
  email: z.string().trim().email("Ugyldig e-postadresse"),
  password: z.string().min(6, "Passord må være minst 6 tegn"),
});

export function LoginForm() {
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const { toast } = useToast();

  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      const trimmedData = {
        email: loginData.email.trim(),
        password: loginData.password
      };
      
      loginSchema.parse(trimmedData);
      const { error } = await signIn(trimmedData.email, trimmedData.password);

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          toast({
            title: "Feil",
            description: "Ugyldig e-post eller passord",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Feil",
            description: error.message,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Velkommen tilbake!",
          description: "Du er nå logget inn",
        });
        navigate("/");
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Record<string, string> = {};
        error.issues.forEach(issue => {
          const path = issue.path[0] as string;
          fieldErrors[path] = issue.message;
        });
        setErrors(fieldErrors);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleLogin} className="flex flex-col gap-6">
      <div className="flex flex-col items-center gap-2 text-center">
        <h1 className="text-2xl font-bold">Logg inn</h1>
        <p className="text-balance text-sm text-muted-foreground">
          Skriv inn e-posten din nedenfor for å logge inn
        </p>
      </div>
      <div className="grid gap-6">
        <Field data-invalid={!!errors.email}>
          <FieldLabel htmlFor="email">E-post</FieldLabel>
          <Input
            id="email"
            type="email"
            placeholder="din@epost.no"
            value={loginData.email}
            onChange={(e) => {
              setLoginData({ ...loginData, email: e.target.value });
              if (errors.email) setErrors({ ...errors, email: "" });
            }}
            aria-invalid={!!errors.email}
          />
          <FieldError>{errors.email}</FieldError>
        </Field>
        
        <Field data-invalid={!!errors.password}>
          <FieldLabel htmlFor="password">Passord</FieldLabel>
          <Input
            id="password"
            type="password"
            value={loginData.password}
            onChange={(e) => {
              setLoginData({ ...loginData, password: e.target.value });
              if (errors.password) setErrors({ ...errors, password: "" });
            }}
            aria-invalid={!!errors.password}
          />
          <FieldError>{errors.password}</FieldError>
        </Field>
        
        <Button type="submit" className="w-full" disabled={isLoading}>
          {isLoading ? "Logger inn..." : "Logg inn"}
        </Button>
      </div>
    </form>
  );
}
